import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { SYSTEM_MAINTAINANCE_TAB_OPTIONS } from 'src/app/lib/constants/tabs.constants';
import { ApiService } from 'src/app/services/api.service';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { MegapayLoaderComponent } from 'src/app/shared/component/megapay-loader/megapay-loader.component';
import { SharedComponentsModule } from 'src/app/shared/modules/shared-component.module';
import { SharedDirectivesModule } from 'src/app/shared/modules/shared-directives.module';
import { DropdownFieldComponent } from 'src/app/shared/component/custom-dropdown/custom-dropdown.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as vo from 'src/app/core/model/server/vo/_index';

@Component({
  selector: 'app-cashout-configuration',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedComponentsModule,
    SharedDirectivesModule,
    NgxSpinnerModule,
    MegapayLoaderComponent,
    DropdownFieldComponent,
    MatTooltipModule,
  ],
  templateUrl: './cashout-configuration.component.html',
  styleUrl: './cashout-configuration.component.scss'
})
export class CashoutConfigurationComponent implements OnInit {

  tabs = SYSTEM_MAINTAINANCE_TAB_OPTIONS;
  isLoading = false;


  // cashout limits states
  isLoadingTiers  = false;
  isLoadingLimits = false;

  tierOptions: { label: string; value: any }[] = [];
  selectedTierId: number | null = null;


  // cashout limits form states
  private loadedLimits: vo.GetCashoutLimitsResponse['result'] | null = null;

  limitForm = {
    minAmountPerTransaction:  null as number | null,
    maxAmountPerTransaction:  null as number | null,
    dailyLimitAmount:         null as number | null,
    monthlyLimitAmount:       null as number | null,
  };

  // Saved baselines from API
  currentKillSwitch = '';
  currentApprovalMode = '';

  // Currently selected values in the dropdowns
  selectedKillSwitch = '';
  selectedApprovalMode = '';

  killSwitchOptions = [
    { label: 'Enabled',  value: 'ENABLED'  },
    { label: 'Disabled', value: 'DISABLED' },
  ];

  approvalModeOptions = [
    { label: 'Auto',   value: 'AUTO'   },
    { label: 'Manual', value: 'MANUAL' },
  ];

  // ─── Label getters ───────────────────────────────────────────────────────────

  get killSwitchText(): string {
    return this.killSwitchOptions.find(
      o => o.value.toUpperCase() === this.selectedKillSwitch?.toUpperCase()
    )?.label ?? '';
  }

  get approvalModeText(): string {
    return this.approvalModeOptions.find(
      o => o.value.toUpperCase() === this.selectedApprovalMode?.toUpperCase()
    )?.label ?? '';
  }

  get selectedTierLabel(): string {
    return this.tierOptions.find(o => o.value === this.selectedTierId)?.label ?? '';
  }

  // ─── Dirty detection getters ─────────────────────────────────────────────────

  /** True when the selected value is the same as what was fetched — no change made */
  get killSwitchUnchanged(): boolean {
    return this.selectedKillSwitch?.toUpperCase() === this.currentKillSwitch?.toUpperCase();
  }

  get approvalModeUnchanged(): boolean {
    return this.selectedApprovalMode?.toUpperCase() === this.currentApprovalMode?.toUpperCase();
  }

  get limitsUnchanged(): boolean {
    if (!this.loadedLimits) {
      return true;
    }

    return (
      this.limitForm.minAmountPerTransaction === (this.loadedLimits.minAmountPerTransaction ?? null) &&
      this.limitForm.maxAmountPerTransaction === (this.loadedLimits.maxAmountPerTransaction ?? null) &&
      this.limitForm.dailyLimitAmount        === (this.loadedLimits.dailyLimitAmount        ?? null) &&
      this.limitForm.monthlyLimitAmount      === (this.loadedLimits.monthlyLimitAmount      ?? null)
    );
  }

  constructor(
    public Toastr: ToastrService,
    private apiService: ApiService,
    public popUpDialog: PopUpDialogService,
    private spinner: NgxSpinnerService,
  ) {}

  ngOnInit(): void {
    this.loadSettings();
    this.loadTiers();   
  }

  // ─── LOAD ────────────────────────────────────────────────────────────────────

  loadSettings(): void {
    this.isLoading = true;
    this.spinner.show().catch((err) => console.log('Error showing spinner', err));

    this.apiService.getCashoutSettings().subscribe({
      next: (res) => {
        if (res?.result) {
          this.selectedKillSwitch   = res.result.disbursementFeatureStatus?.toUpperCase();
          this.selectedApprovalMode = res.result.disbursementApprovalMode?.toUpperCase();
          // Store baseline for dirty detection
          this.currentKillSwitch    = this.selectedKillSwitch;
          this.currentApprovalMode  = this.selectedApprovalMode;
        }
      },
      error: (err) => {
        this.Toastr.error('Failed to load cashout settings. Please try again.');
        this.isLoading = false;
        this.spinner.hide().catch((err) => console.log('Error hiding spinner', err));
      },
      complete: () => {
        this.isLoading = false;
        this.spinner.hide().catch((err) => console.log('Error hiding spinner', err));
      }
    });
  }


  loadTiers(): void {
      this.isLoadingTiers = true;

      this.apiService.getAccountsTiers().subscribe({
        next: (res) => {
          if (res?.result) {
            this.tierOptions = res.result.map(t => ({
              label: `Tier ${t.tierId} (${t.tierName})`,
              value: t.tierId,  
            }));
          }
        },
        error: (err) => {
          this.Toastr.error('Failed to load tiers. Please try again.');
        },
        complete: () => {
          this.isLoadingTiers = false;

          if (this.tierOptions.length > 0) {
            const defaultTier = this.tierOptions[0];
            this.selectedTierId = defaultTier.value;
            this.loadCashoutLimits(this.selectedTierId);
          }

        }
      });
  }

  loadCashoutLimits(tierId: number): void {
    this.isLoadingLimits = true;
    this.spinner.show().catch((err) => console.log('Error showing spinner', err));

    this.apiService.getCashoutLimits(tierId).subscribe({
      next: (res) => {
        const data = res.result;
        this.loadedLimits = data;
        this.limitForm = {
          minAmountPerTransaction:  data?.minAmountPerTransaction  ?? null,
          maxAmountPerTransaction:  data?.maxAmountPerTransaction  ?? null,
          dailyLimitAmount:         data?.dailyLimitAmount         ?? null,
          monthlyLimitAmount:       data?.monthlyLimitAmount       ?? null,
        };
      },
      error: (err) => {
        this.Toastr.error('Failed to load cashout limits. Please try again.');
        this.isLoadingLimits = false;
        this.spinner.hide().catch((err) => console.log('Error hiding spinner', err));
      },
      complete: () => {
        this.isLoadingLimits = false;
        this.spinner.hide().catch((err) => console.log('Error hiding spinner', err));
      }
    });
  }

  // ─── DROPDOWN HANDLERS ───────────────────────────────────────────────────────

  onKillSwitchChange(option: { label: string; value: any }): void {
    this.selectedKillSwitch = option.value;
  }

  onApprovalModeChange(option: { label: string; value: any }): void {
    this.selectedApprovalMode = option.value;
  }

  onTierChange(option: { label: string; value: any }): void {
    this.selectedTierId = option.value;
    this.loadCashoutLimits(this.selectedTierId!);
  }

  // ─── SAVE KILL SWITCH ────────────────────────────────────────────────────────

  saveKillSwitch(): void {
    if (!this.selectedKillSwitch) {
      this.Toastr.error('Please select a Status before saving.');
      return;
    }

    const isDisabling = this.selectedKillSwitch === 'DISABLED'
      && this.currentKillSwitch !== 'DISABLED';

    const confirmMessage = isDisabling
      ? '<strong>Are you sure to disable it?</strong><br><br> Disabling cash out will prevent Megapay users from using the Cashout feature.'
      : '<strong>Are you sure to enable it?</strong><br><br> Enabling cash out will allow Megapay users to use the Cashout feature.';

    this.popUpDialog.openSaveActionDialog(
      confirmMessage,
      () => this.executeUpdate('killSwitch'),
      () => console.log('Save cancelled by user.')
    );
  }

  // ─── SAVE APPROVAL MODE ──────────────────────────────────────────────────────

  saveApprovalMode(): void {
      if (!this.selectedApprovalMode) {
        this.Toastr.error('Please select an Approval Mode before saving.');
        return;
      }
  
      const isSwitchingToManual = this.selectedApprovalMode === 'MANUAL'
        && this.currentApprovalMode !== 'MANUAL';
  
      const confirmMessage = isSwitchingToManual
  ? '<strong>Are you sure to switch to Manual Approval?</strong><br><br>' +
    ' Newly created cashout transactions will now require manual<br>' +
    ' review and approval from an Admin before being processed.'
  : '<strong>Are you sure to switch to Auto Approval?</strong><br><br>' +
    ' Newly created cashout transactions will now automatically<br>' +
    ' being processed real time and no approval required.';
  
      this.popUpDialog.openSaveActionDialog(
        confirmMessage,
        () => this.executeUpdate('approvalMode'),
        () => console.log('Save cancelled by user.')
      );
  }

  // ─── SAVE CASHOUT LIMITS ─────────────────────────────────────────────────────

  saveCashoutLimits(): void {
    if (this.selectedTierId === null){
      return;
    }

    const min = this.limitForm.minAmountPerTransaction;
    const max = this.limitForm.maxAmountPerTransaction;

    if (min !== null && max !== null && min > max) {
      this.Toastr.error('Minimum per transaction cannot be greater than maximum per transaction.');
      return;
    }

    this.popUpDialog.openSaveActionDialog(
      '<strong>Save cashout limits?</strong><br><br>This will update the transaction limits for the selected tier.',
      () => this.executeUpdateLimits(),
      () => console.log('Limits save cancelled.')
    );
  }

  // ─── EXECUTE UPDATE ──────────────────────────────────────────────────────────

  private executeUpdate(field: 'killSwitch' | 'approvalMode'): void {
    const killSwitchMap: Record<string, string> = {
      ENABLED:  'enable',
      DISABLED: 'disable',
    };

    const approvalModeMap: Record<string, string> = {
      AUTO:   'auto',
      MANUAL: 'manual',
    };

    const request: vo.UpdateCashoutSettingsRequest = {
      killSwitchAction: killSwitchMap[this.selectedKillSwitch] ?? this.selectedKillSwitch?.toLowerCase(),
      approvalMode:     approvalModeMap[this.selectedApprovalMode] ?? this.selectedApprovalMode?.toLowerCase(),
    };


    this.isLoading = true;
    this.spinner.show().catch((err) => console.log('Error showing spinner', err));

    this.apiService.updateCashoutSettings(request).subscribe({
      next: (response) => {
        if (response.status !== 200) {
          this.Toastr.error('Failed to save cashout settings. Please try again.');
          return;
        }
        // Sync baselines so Save disables again after a successful update
        this.currentKillSwitch   = this.selectedKillSwitch;
        this.currentApprovalMode = this.selectedApprovalMode;

        const successMsg = field === 'killSwitch'
          ? this.selectedKillSwitch === 'DISABLED'
            ? 'Cashout has been disabled successfully.'
            : 'Cashout has been enabled successfully.'
          : 'Approval mode has been successfully updated.';

        this.Toastr.success(successMsg);
      },
      error: (err) => {
        console.error('Error saving cashout settings:', err);
        this.Toastr.error('Failed to save cashout settings. Please try again.');
      },
      complete: () => {
        this.isLoading = false;
        this.spinner.hide().catch((err) => console.log('Error hiding spinner', err));
      }
    });
  }

  private executeUpdateLimits(): void {
    const toNullIfZero = (val: number | null): number | null =>
      val === 0 || val === null ? null : val;

    const roundTo2 = (val: number | null): number | null =>
      val !== null ? Math.round(val * 100) / 100 : null;

    const request: vo.UpdateCashoutLimitRequest = {
      minAmountPerTransaction:  toNullIfZero(roundTo2(this.limitForm.minAmountPerTransaction)),
      maxAmountPerTransaction:  toNullIfZero(roundTo2(this.limitForm.maxAmountPerTransaction)),
      dailyLimitAmount:         toNullIfZero(roundTo2(this.limitForm.dailyLimitAmount)),
      monthlyLimitAmount:       toNullIfZero(roundTo2(this.limitForm.monthlyLimitAmount)),
    };

    this.isLoadingLimits = true;
    this.spinner.show().catch((err) => console.log('Error showing spinner', err));

    this.apiService.updateCashoutLimits(request, this.selectedTierId!).subscribe({
      next: (res) => {
        if (res.status !== 200) {
          this.Toastr.error('Failed to save cashout limits. Please try again.');
          return;
        }

        const synced = {
          minAmountPerTransaction:  request.minAmountPerTransaction,
          maxAmountPerTransaction:  request.maxAmountPerTransaction,
          dailyLimitAmount:         request.dailyLimitAmount,
          monthlyLimitAmount:       request.monthlyLimitAmount,
        };

        if (this.loadedLimits) {
          this.loadedLimits.minAmountPerTransaction  = request.minAmountPerTransaction;
          this.loadedLimits.maxAmountPerTransaction  = request.maxAmountPerTransaction;
          this.loadedLimits.dailyLimitAmount         = request.dailyLimitAmount;
          this.loadedLimits.monthlyLimitAmount       = request.monthlyLimitAmount;
        }

        this.limitForm = { ...synced };
        this.loadedLimits = { ...this.loadedLimits, ...synced };

        this.Toastr.success('Cashout limits updated successfully.');
      },
      error: (err) => {
        this.Toastr.error('Failed to save cashout limits. Please try again.');
      },
      complete: () => {
        this.isLoadingLimits = false;
        this.spinner.hide().catch((err) => console.log('Error hiding spinner', err));
      }
    });
  }

  
}
